import { ActionButton } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import Close from '@react-spectrum/s2/icons/Close';
import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import {
	Button,
	GridList,
	GridListItem,
	useDragAndDrop,
	type DropIndicatorProps,
	DropIndicator,
} from 'react-aria-components';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { BlobImage } from '@/components/blob/BlobImage';
import { cn } from '@/utils/cn.ts';

import type { InstagramPostContent } from '../../../../bridge/types/artifact.ts';

import { useImageReplace } from './useImageReplace.tsx';

// Mirrors beacon's carousel cap (shared/agents/tools/carousel_tools.py): a
// carousel can hold at most this many slides, so the add tile is disabled once
// the strip is full.
export const MAX_SLIDES = 20;

// Slide keys are `${index}-${url}` composites (see the GridList items below), so
// the position is recovered from the leading integer rather than searching the
// URL list. Two slides can share a URL, so a URL search would find the wrong one
// (or, with composite keys, none at all). The index digits never contain a dash
// and we always prepend `${i}-`, so the first dash is always our separator even
// when the URL itself contains dashes.
const indexFromKey = (key: string): number => {
	const dash = key.indexOf('-');
	const n = dash < 0 ? NaN : Number(key.slice(0, dash));
	return Number.isInteger(n) ? n : -1;
};

// Translates a react-aria drop (a target slide + before/after position) into the
// from/to indices reorderSlideContent expects. That helper removes `from` first
// and then splices at `to`, so when the insertion point sits after the dragged
// slide it shifts down by one once the slide is pulled out. Returns null when
// the move is a no-op (dropped onto its own edge). Exported for unit testing —
// simulating HTML5 drag events in jsdom is impractical.
// eslint-disable-next-line react-refresh/only-export-components
export const computeReorderIndices = (
	slides: string[],
	fromKey: string,
	targetKey: string,
	dropPosition: 'before' | 'after',
): { fromIndex: number; toIndex: number } | null => {
	const fromIndex = indexFromKey(fromKey);
	const targetIndex = indexFromKey(targetKey);
	if (
		fromIndex < 0 ||
		fromIndex >= slides.length ||
		targetIndex < 0 ||
		targetIndex >= slides.length
	)
		return null;
	const insertAt = dropPosition === 'after' ? targetIndex + 1 : targetIndex;
	const toIndex = insertAt > fromIndex ? insertAt - 1 : insertAt;
	if (toIndex === fromIndex) return null;
	return { fromIndex, toIndex };
};

interface SlideFilmstripProps {
	artifactId: string;
	content: InstagramPostContent;
	selectedIndex: number;
	isReadOnly?: boolean;
	/**
	 * Set while an async slide edit is in flight. A structural change racing a
	 * vibe edit (which captured a now-stale image_index) would write the wrong
	 * slide, so reorder, delete, and add are all suspended until it lands.
	 */
	disabled?: boolean;
}

export const SlideFilmstrip = ({
	artifactId,
	content,
	selectedIndex,
	isReadOnly = false,
	disabled = false,
}: SlideFilmstripProps) => {
	const bridgeApi = useBridgeApi();
	const slides = content.image_urls ?? [];

	const canEdit = !isReadOnly && !disabled;
	const atCap = slides.length >= MAX_SLIDES;

	const { openPicker, elements: addElements } = useImageReplace({
		artifactId,
		mode: 'insert',
		atIndex: slides.length,
		aspect: '4:5',
	});

	const setSlide = (index: number) => {
		bridgeApi.sendAction({
			type: 'do_set_chat_panel_slide',
			slide_index: index,
		});
	};

	const removeSlide = (index: number) => {
		bridgeApi.sendAction({
			type: 'do_remove_slide',
			artifact_id: artifactId,
			image_index: index,
		});
	};

	// Keep the active thumbnail centered in the viewport — the same technique
	// the large preview uses. When the strip is narrower than the viewport the
	// inner track's auto margins center it and scrollTo is a no-op; once it
	// overflows the margins collapse and this drives the rotating experience.
	const viewportRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<(HTMLElement | null)[]>([]);
	const hasPositionedRef = useRef(false);

	useLayoutEffect(() => {
		const viewport = viewportRef.current;
		const item = itemRefs.current[selectedIndex];
		if (!viewport || !item) return;
		const target =
			item.offsetLeft - (viewport.clientWidth - item.clientWidth) / 2;
		viewport.scrollTo({
			left: Math.max(0, target),
			behavior: hasPositionedRef.current ? 'smooth' : 'auto',
		});
		hasPositionedRef.current = true;
	}, [selectedIndex, slides.length]);

	// Slide URLs are NOT guaranteed unique — the "add slide" tool duplicates an
	// existing slide's image as an editable copy — so the react-aria key composes
	// the slide index with the URL to keep the collection's keyMap one-to-one.
	// Two items sharing a key corrupts react-aria's internal collection and
	// crashes the render.
	// Always pass dragAndDropHooks to GridList — react-aria calls internal hooks
	// conditionally based on its presence. Guard the reorder callback so drags
	// are inert when editing is disabled.
	const { dragAndDropHooks } = useDragAndDrop({
		getItems: (keys) => [...keys].map((key) => ({ 'text/plain': String(key) })),
		renderDropIndicator: (target) => (
			<DropIndicator
				target={target as DropIndicatorProps['target']}
				className={({ isDropTarget }) =>
					cn(
						'mx-0.5 w-0.5 self-stretch rounded-full',
						isDropTarget ? 'bg-(--text-primary)' : 'bg-transparent',
					)
				}
			/>
		),
		onReorder: (e) => {
			if (!canEdit) return;
			const fromKey = [...e.keys][0];
			if (fromKey === undefined) return;
			const indices = computeReorderIndices(
				slides,
				String(fromKey),
				String(e.target.key),
				e.target.dropPosition === 'after' ? 'after' : 'before',
			);
			if (!indices) return;
			bridgeApi.sendAction({
				type: 'do_reorder_slides',
				artifact_id: artifactId,
				from_index: indices.fromIndex,
				to_index: indices.toIndex,
			});
		},
	});

	if (slides.length === 0) return null;

	return (
		// Full-width scroll viewport; the inner track centers via mx-auto when it
		// fits and scrolls when it overflows. The viewport's padding gives the
		// selected-slide ring room so overflow clipping never shears it.
		<div
			ref={viewportRef}
			className="scrollbar-hidden relative flex w-full overflow-x-auto"
		>
			<div className="mx-auto flex items-center gap-2 px-4 py-2">
				<GridList
					aria-label="Carousel slides"
					items={slides.map((url, i) => ({ id: `${i}-${url}`, url, index: i }))}
					layout="grid"
					className="flex flex-row items-center gap-2"
					dragAndDropHooks={dragAndDropHooks}
				>
					{(item) => {
						const isActive = item.index === selectedIndex;
						return (
							<GridListItem
								ref={(el) => {
									itemRefs.current[item.index] = el;
								}}
								textValue={`Slide ${item.index + 1}`}
								className={cn(
									`group relative h-16 w-16 flex-shrink-0 cursor-pointer
									overflow-hidden rounded-lg transition-transform duration-150
									outline-none data-[dragging]:scale-95
									data-[dragging]:opacity-50`,
									isActive
										? 'ring-2 ring-(--text-primary)'
										: 'ring-1 ring-black/10 hover:ring-black/30',
								)}
								onAction={() => setSlide(item.index)}
							>
								<BlobImage
									src={item.url}
									alt={`Slide ${item.index + 1}`}
									className="h-full w-full object-cover"
								/>
								{canEdit && (
									<Button
										slot={null}
										aria-label={`Remove slide ${item.index + 1}`}
										onPress={() => removeSlide(item.index)}
										// S2 workflow icons render via --iconPrimary, not
										// currentColor, so set it here to make the X white.
										style={{ '--iconPrimary': '#ffffff' } as CSSProperties}
										className="absolute top-0.5 right-0.5 flex size-5
											items-center justify-center rounded-full bg-black/60
											opacity-0 transition-opacity outline-none
											group-hover:opacity-100 data-[focus-visible]:opacity-100"
									>
										<Close
											UNSAFE_style={{ width: '0.75rem', height: '0.75rem' }}
										/>
									</Button>
								)}
							</GridListItem>
						);
					}}
				</GridList>

				{canEdit && (
					<>
						<ActionButton
							aria-label="Add slide"
							isDisabled={atCap}
							onPress={openPicker}
							UNSAFE_className={addTileClass}
						>
							<Add />
						</ActionButton>
						{addElements}
					</>
				)}
			</div>
		</div>
	);
};

// Dashed "+" tile that opens the upload picker. S2 ActionButton styling is
// replaced wholesale via UNSAFE_className so the tile matches the slide thumbnails.
const addTileClass = cn(
	`flex size-16 flex-shrink-0 items-center justify-center rounded-lg border-2
	border-dashed border-black/20 bg-transparent text-gray-600 transition-colors
	outline-none hover:border-black/40 hover:text-gray-900
	disabled:cursor-not-allowed disabled:opacity-40`,
);
